import React, { CSSProperties, useMemo } from "react";

import optionalFieldProcessor from "../../../shared/translation/audit-log/processor";
import initialise from "../../../shared/translation/initialisation";
import globalResources from "./common/en-GB.json";

const i18n = initialise(optionalFieldProcessor);

importTranslations("translation", { "en-GB": globalResources });

export type DashboardLanguage = "en-GB";
export type TranslationSet = { [key: string]: string | TranslationSet };

type TranslationOption = string | number;

interface FullTranslatable {
  t: string;
  [option: string]: TranslationOption;
}

/** This can be: an object with key and options, just the key as a string, or at a pinch, just the untranslatable text as a string */
export type Translatable = string | FullTranslatable;

export function useTranslate() {
  const _translate = i18n.useTranslate();
  // We can't use useCallback here because there are too many signatures to define
  return useMemo(() => {
    /** Translates the string based on the key and the arguments */
    function translate(key: string, args: { [option: string]: TranslationOption }): string;
    /** Translates the string based on the key if it exists */
    function translate(keyOrUntranslatedText: Translatable): string;
    /** Translates the string based on the key if it exists, or returns null if the key was missing */
    function translate(key: Translatable | null | undefined): string | null;
    function translate(options?: Translatable | null | undefined, args: { [option: string]: TranslationOption } = {}) {
      if (options === null || options === undefined) {
        return null;
      }
      if (typeof options === "string") return _translate(options, args);
      const { t, ...opts } = options;
      return _translate(t, { ...opts, ...args });
    }
    return translate;
  }, [_translate]);
}

/**
 * <Translation t="someString" param="4" />
 * or if you've got a translatable in a variable then
 * <Translation props={translatable} />
 **/
export function Translation(props: FullTranslatable | { props: Translatable | null | undefined }) {
  const translate = useTranslate();
  return <>{translate("t" in props ? props : props.props)}</>;
}

/** Just a <Translation> wrapped in a <tag>, because it comes up quite a bit. Not you can't use <TextElement tag="p" t="someString" param="4" /> because TypeScript gets upset — use <TextElement tag="p" props={{ t: "someString", param: "4" }} /> instead. */
export function TextElement({
  className,
  style,
  tag: Tag,
  ...props
}: { className?: string; style?: CSSProperties; tag: "p" | "h2" | "h3" | "span" } & (
  | { t: string }
  | { props: Translatable | null | undefined }
)) {
  return (
    <Tag className={className} style={style}>
      <Translation {...props} />
    </Tag>
  );
}

export function importTranslations(namespace: string, translations: Record<DashboardLanguage, TranslationSet>) {
  for (const language in translations) {
    if (i18n.i18next.hasResourceBundle(language, namespace)) continue;
    i18n.i18next.addResourceBundle(language, namespace, translations[language as DashboardLanguage]);
  }
}
